import React, { useState, useEffect, useCallback } from 'react';
import { consultationAPI } from '../../utils/api';
import { formatDate } from '../../utils/formatters';
import { Button } from '../ui/button';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '../ui/table';

// Add Dialog components for confirmation
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

interface Consultation {
  _id: string;
  name: string;
  email: string;
  phone: string;
  dogName: string;
  dogBreed: string;
  dogAge: number;
  message: string;
  preferredDate: string;
  status: 'pending' | 'confirmed' | 'completed' | 'cancelled';
  createdAt: string;
  notes?: string;
}

const AdminConsultations: React.FC = () => {
  const [consultations, setConsultations] = useState<Consultation[]>([]);
  const [selectedConsultation, setSelectedConsultation] = useState<Consultation | null>(null);
  const [filter, setFilter] = useState<string>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Add state for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [consultationToDelete, setConsultationToDelete] = useState<string | null>(null);
  // Add state for error message in delete dialog
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // Add state for notes
  const [notes, setNotes] = useState<string>('');
  // Add state for saving notes
  const [isSavingNotes, setIsSavingNotes] = useState(false);

  const fetchConsultations = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await consultationAPI.getConsultations(1, 100, filter !== 'all' ? filter : undefined);
      setConsultations(response.data.consultations);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Kunne ikke hente konsultationer');
    } finally {
      setIsLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchConsultations();
  }, [filter, fetchConsultations]);

  // Set notes when a consultation is selected
  useEffect(() => {
    if (selectedConsultation) {
      setNotes(selectedConsultation.notes || '');
    } else {
      setNotes('');
    }
  }, [selectedConsultation]);

  const handleStatusChange = async (id: string, newStatus: string) => {
    try {
      await consultationAPI.updateConsultation(id, { status: newStatus });
      
      // Update the local state
      setConsultations(prevConsultations => 
        prevConsultations.map(consultation => 
          consultation._id === id 
            ? { ...consultation, status: newStatus as any } 
            : consultation
        )
      );
      
      if (selectedConsultation?._id === id) {
        setSelectedConsultation(prev => prev ? { ...prev, status: newStatus as any } : null);
      }
    } catch (err) {
      console.error('Failed to update consultation status', err);
    }
  };

  // Add function to handle notes change
  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNotes(e.target.value);
  };

  // Add function to save notes
  const handleSaveNotes = async () => {
    if (!selectedConsultation) return;
    
    try {
      setIsSavingNotes(true);
      
      await consultationAPI.updateConsultation(selectedConsultation._id, { notes });
      
      // Update the local state
      setConsultations(prevConsultations => 
        prevConsultations.map(consultation => 
          consultation._id === selectedConsultation._id 
            ? { ...consultation, notes } 
            : consultation
        )
      );
      
      // Update the selected consultation
      setSelectedConsultation(prev => prev ? { ...prev, notes } : null);
      
      // Show a brief success message or toast here if you have a toast component
      console.log('Notes saved successfully');
    } catch (err) {
      console.error('Failed to save notes', err);
    } finally {
      setIsSavingNotes(false);
    }
  };

  // Add function to handle delete confirmation
  const handleDeleteClick = (id: string) => {
    setConsultationToDelete(id);
    setDeleteDialogOpen(true);
  };

  // Add function to handle actual deletion
  const handleDeleteConfirm = async () => {
    if (!consultationToDelete) return;
    
    try {
      // Reset any previous error
      setDeleteError(null);
      
      await consultationAPI.deleteConsultation(consultationToDelete);
      
      // Update the local state by removing the deleted consultation
      setConsultations(prevConsultations => 
        prevConsultations.filter(consultation => consultation._id !== consultationToDelete)
      );
      
      // If the deleted consultation was selected, clear the selection
      if (selectedConsultation?._id === consultationToDelete) {
        setSelectedConsultation(null);
      }
      
      // Close the dialog
      setDeleteDialogOpen(false);
      setConsultationToDelete(null);
    } catch (err: any) {
      console.error('Failed to delete consultation', err);
      // Set error message for display in the dialog
      setDeleteError(
        err.response?.data?.message || 
        'Der opstod en fejl ved sletning af konsultationen. Prøv igen senere.'
      );
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'confirmed':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Afventer';
      case 'confirmed':
        return 'Bekræftet';
      case 'completed':
        return 'Gennemført';
      case 'cancelled':
        return 'Annulleret';
      default:
        return status;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-snudefryd-blue"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <h2 className="text-2xl font-bold text-gray-800">Konsultationer</h2>
        <div className="flex flex-wrap gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-snudefryd-blue"
          >
            <option value="all">Alle</option>
            <option value="pending">Afventer</option>
            <option value="confirmed">Bekræftet</option>
            <option value="completed">Gennemført</option>
            <option value="cancelled">Annulleret</option>
          </select>
          <Button onClick={() => fetchConsultations()}>Opdater</Button>
        </div>
      </div>

      {consultations.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <p className="text-gray-600">Ingen konsultationer fundet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Mobile view - consultations list */}
          <div className="lg:col-span-2">
            {/* Mobile card view */}
            <div className="lg:hidden space-y-4">
              {consultations.map((consultation) => (
                <div 
                  key={consultation._id} 
                  className={`bg-white rounded-lg shadow p-4 ${
                    selectedConsultation?._id === consultation._id ? 'border-2 border-snudefryd-purple' : ''
                  }`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-medium">{consultation.name}</div>
                      <div className="text-sm text-gray-500">{formatDate(consultation.preferredDate)}</div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadgeClass(consultation.status)}`}>
                      {getStatusText(consultation.status)}
                    </span>
                  </div>
                  <div className="text-sm mb-3">
                    <span className="font-medium">Hund: </span>
                    {consultation.dogName} ({consultation.dogBreed})
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setSelectedConsultation(
                      selectedConsultation?._id === consultation._id ? null : consultation
                    )}
                    className="w-full justify-center"
                  >
                    {selectedConsultation?._id === consultation._id ? 'Skjul detaljer' : 'Vis detaljer'}
                  </Button>
                  
                  {/* Expanded details on mobile */}
                  {selectedConsultation?._id === consultation._id && (
                    <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className="text-xs text-gray-500">Email</p>
                          <p className="text-sm font-medium break-words">{selectedConsultation.email}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Telefon</p>
                          <p className="text-sm font-medium">{selectedConsultation.phone}</p>
                        </div>
                      </div>
                      
                      <div>
                        <p className="text-xs text-gray-500">Alder</p>
                        <p className="text-sm font-medium">{selectedConsultation.dogAge} år</p>
                      </div>
                      
                      <div>
                        <p className="text-xs text-gray-500">Besked</p>
                        <p className="text-sm mt-1 p-2 bg-gray-50 rounded-md">{selectedConsultation.message}</p>
                      </div>
                      
                      {/* Add notes field */}
                      <div>
                        <p className="text-xs text-gray-500">Noter</p>
                        <textarea
                          value={notes}
                          onChange={handleNotesChange}
                          className="mt-1 w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-snudefryd-blue"
                          rows={4}
                          placeholder="Tilføj noter om konsultationen her..."
                        />
                        <div className="mt-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={handleSaveNotes}
                            disabled={isSavingNotes}
                            className="w-full justify-center"
                          >
                            {isSavingNotes ? 'Gemmer...' : 'Gem noter'}
                          </Button>
                        </div>
                      </div>
                      
                      <div>
                        <p className="text-xs text-gray-500">Status</p>
                        <select
                          value={selectedConsultation.status}
                          onChange={(e) => handleStatusChange(selectedConsultation._id, e.target.value)}
                          className="mt-1 w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-snudefryd-blue"
                        >
                          <option value="pending">Afventer</option>
                          <option value="confirmed">Bekræftet</option>
                          <option value="completed">Gennemført</option>
                          <option value="cancelled">Annulleret</option>
                        </select>
                      </div>
                      
                      {/* Add delete button to mobile view */}
                      <div className="pt-2">
                        <Button 
                          variant="destructive" 
                          size="sm"
                          onClick={() => handleDeleteClick(selectedConsultation._id)}
                          className="w-full justify-center"
                        >
                          Slet konsultation
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            {/* Desktop table view */}
            <div className="hidden lg:block bg-white rounded-lg shadow overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Dato</TableHead>
                    <TableHead>Navn</TableHead>
                    <TableHead>Hund</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Handling</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {consultations.map((consultation) => (
                    <TableRow 
                      key={consultation._id}
                      className={selectedConsultation?._id === consultation._id ? 'bg-snudefryd-light-purple/10' : ''}
                    >
                      <TableCell>{formatDate(consultation.preferredDate)}</TableCell>
                      <TableCell>{consultation.name}</TableCell>
                      <TableCell>{consultation.dogName}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadgeClass(consultation.status)}`}>
                          {getStatusText(consultation.status)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex space-x-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => setSelectedConsultation(consultation)}
                          >
                            Vis detaljer
                          </Button>
                          <Button 
                            variant="destructive" 
                            size="sm"
                            onClick={() => handleDeleteClick(consultation._id)}
                          >
                            Slet
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Desktop sidebar - consultation details */}
          <div className="hidden lg:block lg:col-span-1">
            {selectedConsultation ? (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Konsultationsdetaljer</h3>
                
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-500">Kunde</p>
                    <p className="font-medium">{selectedConsultation.name}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Email</p>
                      <p className="font-medium break-words">{selectedConsultation.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Telefon</p>
                      <p className="font-medium">{selectedConsultation.phone}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Hundens navn</p>
                      <p className="font-medium">{selectedConsultation.dogName}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Race</p>
                      <p className="font-medium">{selectedConsultation.dogBreed}</p>
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">Alder</p>
                    <p className="font-medium">{selectedConsultation.dogAge} år</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">Foretrukken dato</p>
                    <p className="font-medium">{formatDate(selectedConsultation.preferredDate)}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">Besked</p>
                    <p className="mt-1 p-3 bg-gray-50 rounded-md">{selectedConsultation.message}</p>
                  </div>
                  
                  {/* Add notes field */}
                  <div>
                    <p className="text-sm text-gray-500">Noter</p>
                    <textarea
                      value={notes}
                      onChange={handleNotesChange}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-snudefryd-blue"
                      rows={4}
                      placeholder="Tilføj noter om konsultationen her..."
                    />
                    <div className="mt-2">
                      <Button 
                        variant="outline" 
                        onClick={handleSaveNotes}
                        disabled={isSavingNotes}
                        className="w-full"
                      >
                        {isSavingNotes ? 'Gemmer...' : 'Gem noter'}
                      </Button>
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">Status</p>
                    <div className="mt-2">
                      <select
                        value={selectedConsultation.status}
                        onChange={(e) => handleStatusChange(selectedConsultation._id, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-snudefryd-blue"
                      >
                        <option value="pending">Afventer</option>
                        <option value="confirmed">Bekræftet</option>
                        <option value="completed">Gennemført</option>
                        <option value="cancelled">Annulleret</option>
                      </select>
                    </div>
                  </div>
                  
                  {/* Add delete button to desktop view */}
                  <div className="pt-2">
                    <Button 
                      variant="destructive" 
                      onClick={() => handleDeleteClick(selectedConsultation._id)}
                      className="w-full"
                    >
                      Slet konsultation
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center h-full flex items-center justify-center">
                <p className="text-gray-600">Vælg en konsultation for at se detaljer</p>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(open) => {
        setDeleteDialogOpen(open);
        if (!open) {
          // Reset error when dialog is closed
          setDeleteError(null);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Slet konsultation</DialogTitle>
            <DialogDescription>
              Er du sikker på, at du vil slette denne konsultation? Denne handling kan ikke fortrydes.
            </DialogDescription>
          </DialogHeader>
          
          {/* Show error message if deletion failed */}
          {deleteError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm mt-2">
              {deleteError}
            </div>
          )}
          
          <DialogFooter className="flex space-x-2 justify-end">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Annuller
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm}>
              Slet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminConsultations; 